import {type Key, useMemo} from 'react';

import {useAppSelector} from 'stores/redux_store';

import {TabItem, Tabs} from '@time-webkit/all/organisms/tabs';

import {getEmojiUrlByName} from 'utils/emoji_utils';
import {makeGetUniqueReactionsToPost} from 'utils/post_utils';
import {localizeMessage} from 'utils/utils';

import {EmojiWithTooltip} from './emoji_with_tooltip';
import styles from './styles.module.css';

interface ReactionTabsProps {
    postId: string;
    onSelectionChange: (v: Key) => void;
    selectedKey: Key;
    isMobile: boolean;
}

const DESKTOP_MAX_TABS_VISIBLE = 6;
const MOBILE_MAX_TABS_VISIBLE = 3;

export const ReactionTabs: React.FC<ReactionTabsProps> = ({postId, onSelectionChange, selectedKey, isMobile}) => {
    const state = useAppSelector((state) => state);
    const getReactionsForPost = makeGetUniqueReactionsToPost();
    const tabsVisible = isMobile ? MOBILE_MAX_TABS_VISIBLE : DESKTOP_MAX_TABS_VISIBLE;

    const tabsReactions = Object.values(getReactionsForPost(state, postId) || {});

    const tabs = useMemo(() => {
        const reactionCounts = tabsReactions.reduce<Record<string, number>>((acc, reaction) => {
            return {
                ...acc,
                [reaction.emoji_name]: acc[reaction.emoji_name] ? acc[reaction.emoji_name]! + 1 : 1,
            };
        }, {});

        return Object.entries(reactionCounts).map(([name, count], index) => {
            const className = index + 1 >= tabsVisible ? styles.dropdown__title : styles.tabs__title;
            const emojiUrl = getEmojiUrlByName(state, name);
            return {
                name,
                count,
                emojiUrl,
                className,
            };
        });
    }, [state, tabsVisible, tabsReactions]);

    return (
        <div className={styles.tabsContainer}>
            <Tabs
                moreLabel={localizeMessage('post_info.reactions.more', 'More')}
                maxTabsVisible={tabsVisible}
                onSelectionChange={onSelectionChange}
                selectedKey={selectedKey}
            >
                {[
                    <TabItem
                        key='all'
                        title={
                            <TabTitle
                                count={tabsReactions.length}
                                title={`${localizeMessage('post_info.reactions.all-reactions', 'All')} `}
                            />
                        }
                    />,
                    ...tabs.map((tab) => {
                        return (
                            <TabItem
                                key={tab.name}
                                title={
                                    <TabTitle
                                        count={tab.count}
                                        className={tab.className}
                                        title={
                                            <EmojiWithTooltip
                                                emojiName={tab.name}
                                                emojiUrl={tab.emojiUrl}
                                            />
                                        }
                                    />
                                }
                            />
                        );
                    }),
                ]}
            </Tabs>
        </div>
    );
};

const TabTitle = ({count, className, title}: {count: number; title: React.ReactNode; className?: string}) => {
    return (
        <div className={className}>
            {title}

            <span>{count > 999 ? '999+' : count}</span>
        </div>
    );
};
